import { TFunction } from 'react-i18next';
import React, { ReactNode } from 'react';

interface ErrorMessageOptions {
  preventPleaseContactText?: boolean;
  errorLogId?: string | null;
}

export function createEnglishFormalErrorMessage(errorReason: ReactNode, options?: ErrorMessageOptions): string;
export function createEnglishFormalErrorMessage(errorReason: ReactNode, errorLogId?: string | null): string;
export function createEnglishFormalErrorMessage(
  errorReason: ReactNode,
  options?: string | ErrorMessageOptions | null,
): ReactNode {
  const { preventPleaseContactText, errorLogId }: ErrorMessageOptions =
    typeof options === 'string' ? { errorLogId: options } : (options ?? {});

  const pleaseContactText = preventPleaseContactText ? null : 'Please contact us at support@nsknox.net for assistance.';
  return createFormalErrorMessage(errorReason, pleaseContactText, errorLogId && `Refer to error code: ${errorLogId}.`);
}

export function createTranslatedFormalErrorMessage(
  t: TFunction<string>,
  errorReasonTranslationKey: string,
  pleaseContactTranslationKey: string,
  referToErrorCodeTranslationKey?: string,
  errorLogId?: string | null,
): ReactNode {
  try {
    return createFormalErrorMessage(
      t(errorReasonTranslationKey),
      t(pleaseContactTranslationKey),
      errorLogId && referToErrorCodeTranslationKey && t(referToErrorCodeTranslationKey, { errorLogId }),
    );
  } catch {
    const logId = errorLogId ? `(${errorLogId})` : '';
    return `Unexpected error occurred while trying to login. ${logId}`;
  }
}

function createFormalErrorMessage(
  errorReason: ReactNode,
  pleaseContactText: string | null,
  referToErrorCodeText: string | null | undefined,
): ReactNode {
  return (
    <span>
      {errorReason}
      <br />
      {pleaseContactText}
      {referToErrorCodeText && (
        <>
          <br />
          {referToErrorCodeText}
        </>
      )}
    </span>
  );
}

// Appends the web error code and its additional data (if any) to an existing error reason, so the
// error notification exposes the server-side error details on top of the human-readable message.
export function appendWebErrorDetailsToReason(baseReason: ReactNode, errorCode: string, additionalData: unknown): ReactNode {
  const detailLines = buildWebErrorDetailLines(errorCode, additionalData);

  return (
    <span>
      {baseReason}
      {detailLines.map((line) => (
        <React.Fragment key={line}>
          <br />
          {line}
        </React.Fragment>
      ))}
    </span>
  );
}

export function buildWebErrorDetailLines(errorCode: string, additionalData: unknown): string[] {
  const lines = [`Error code: ${errorCode}`];

  const additionalDataEntries = getPlainObjectEntries(additionalData);
  if (additionalDataEntries.length > 0) {
    lines.push('Additional data:');
    additionalDataEntries.forEach(([key, value]) => lines.push(`${key}: ${formatAdditionalDataValue(value)}`));
  }

  return lines;
}

function getPlainObjectEntries(value: unknown): [string, unknown][] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value as Record<string, unknown>);
}

export function formatAdditionalDataValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((item) => formatAdditionalDataValue(item)).join(', ');
  }

  if (value !== null && typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}
